import { log } from './log'
import { extractValueOrTransform } from '@/services/object-utils'

export class ApiService {
  jsonApi = null
  blobApi = null
  formApi = null

  constructor({ jsonApi, blobApi, formApi }) {
    this.blobApi = blobApi
    this.jsonApi = jsonApi
    this.formApi = formApi
  }

  getUploadUri(serviceUri) {
    return `${serviceUri}/upload`
  }

  getSaveShortcutUri() {
    return '/filters'
  }

  getDataUploadsUri(serviceUri) {
    return `${serviceUri}/data-uploads`
  }

  getFluxUri(serviceUri) {
    return `${serviceUri}/flux`
  }

  getCountUri(serviceUri) {
    return `${serviceUri}/count`
  }

  getShortcutUri(serviceUri) {
    return `/filters?uri=${serviceUri}&selectedStatuses=ACTIVE`
  }

  saveForm(serviceUri, item, isNew) {
    return isNew ? this.jsonApi.Put(serviceUri, item) : this.jsonApi.Post(`${serviceUri}/${item.externalId}`, item)
  }

  async isUnique(serviceUri, params) {
    try {
      let resData = await this.jsonApi.Get(`${serviceUri}/exists`, { params })
      const unique = resData.success && !resData.result.valid
      log('Inside API service isUnique method get request try block, params, response data ', params, resData, unique)
      return unique
    } catch (err) {
      log('Inside API service isUnique method, error ', err)
    }
  }

  async page(serviceUri, params, payloadFormat) {
    const fluxUri = this.getFluxUri(serviceUri)
    const selectedApi = payloadFormat === 'jsonApi' ? this.jsonApi : this.formApi
    const pageResponse = await selectedApi.Post(fluxUri, params)
    const items = pageResponse?.items || []
    const count = pageResponse?.count ?? items.length
    const obj = {
      items,
      count,
    }
    return obj
  }

  export(serviceUri, params) {
    const exportUri = `${serviceUri}/export.${params.exportType}`
    return this.blobApi.Get(exportUri, {
      enableDownload: true,
      responseType: 'blob',
      params,
    })
  }

  template(serviceUri, params) {
    const templateUri = `${serviceUri}/data-uploads/template`
    return this.blobApi.Get(templateUri, {
      enableDownload: true,
      responseType: 'blob',
      params,
    })
  }

  downloadExcelFile(serviceUri, item, params) {
    const downloadUri = `${serviceUri}/data-uploads/${item.externalId}/input-file`
    return this.blobApi.Get(downloadUri, {
      enableDownload: true,
      responseType: 'blob',
      params,
    })
  }

  processResultFile(serviceUri, item, params) {
    const resultFileUri = `${serviceUri}/data-uploads/${item.externalId}/results-file`

    return this.blobApi.Get(resultFileUri, {
      enableDownload: true,
      responseType: 'blob',
      params,
    })
  }

  async requestGet(serviceUri, paramsObj) {
    const items = new Set()
    const keys = paramsObj?.externalIds || paramsObj?.selectedExternalIds || []
    if (keys.length > 0) {
      const response = await this.jsonApi.Get(serviceUri, { params: { externalIds: keys } })
      const keyItems = response?.items || (Array.isArray(response) ? response : [])
      keyItems.forEach((item) => items.add(item))
    }
    const response = await this.jsonApi.Get(serviceUri, {
      params: {
        search: paramsObj?.search,
        pageIndex: paramsObj?.pageIndex,
        pageSize: this.#getPageSize(paramsObj) - keys.length,
      },
    })
    const additionalItems = response?.items || (Array.isArray(response) ? response : [])
    additionalItems.forEach((item) => items.add(item))
    return Array.from(items)
  }

  async requestGetPromise(serviceUri, paramsObj) {
    const promise = await this.jsonApi.Get(serviceUri, {
      params: {
        search: paramsObj?.search,
        pageIndex: paramsObj?.pageIndex,
        pageSize: this.#getPageSize(paramsObj),
        ...paramsObj,
      },
    })

    return promise
  }
  async requestGetParams(serviceUri, paramsObj) {
    const response = await this.jsonApi.Get(serviceUri, {
      params: {
        ...paramsObj,
      },
    })

    const additionalItems = response?.items || (Array.isArray(response) ? response : [])
    return Array.from(additionalItems)
  }

  async requestFlavorLiteData(serviceUri) {
    const response = await this.jsonApi.Get(serviceUri, { params: { flavor: 'LITE' } })

    const additionalItems = response?.items
    return Array.from(additionalItems)
  }

  requestPost(serviceUri, params) {
    return this.jsonApi.Post(serviceUri, params)
  }

  getRowsCount(serviceUri, params) {
    const countUri = this.getCountUri(serviceUri)
    return this.jsonApi.Get(countUri, params)
  }

  getDataUploadsHistory(serviceUri, params) {
    const dataUploadsUri = this.getDataUploadsUri(serviceUri)
    return this.jsonApi.Get(dataUploadsUri, { params })
  }

  getShortcuts(serviceUri) {
    const shortcutUri = this.getShortcutUri(serviceUri)
    return this.jsonApi.Get(shortcutUri)
  }

  deleteShortcut(uuid) {
    return this.jsonApi.Delete(`/filters/${uuid}`)
  }

  saveAsShortcut(params) {
    const saveShortcutUri = this.saveShortcutUri()
    return this.jsonApi.Post(saveShortcutUri, params)
  }

  uploadExcel(serviceUri, params) {
    const uploadUri = this.getUploadUri(serviceUri)
    return this.jsonApi.Post(uploadUri, params)
  }

  activate(serviceUri, item) {
    const unqId = item.externalId ?? item.internalId
    const activateUri = `${serviceUri}/${unqId}/activate`
    return this.jsonApi.Post(activateUri)
  }

  deactivate(serviceUri, item) {
    const unqId = item.externalId ?? item.internalId
    const deactivateUri = `${serviceUri}/${unqId}/deactivate`
    return this.jsonApi.Post(deactivateUri)
  }

  delete(serviceUri, item) {
    const unqId = item.externalId ?? item.internalId
    const deleteUri = `${serviceUri}/${unqId}/remove`
    return this.jsonApi.Post(deleteUri)
  }

  getItem(serviceUri, item) {
    const unqId = item.externalId ?? item.internalId
    const editUri = `${serviceUri}/mono?q=${unqId}`
    return this.jsonApi.Get(editUri)
  }

  viewHistory(serviceUri, item) {
    const unqId = item.externalId ?? item.internalId
    const uri = `${serviceUri} + '/' + ${unqId} + '/mutations'`
    return this.jsonApi.Get(uri)
  }

  async idbSearch(params, dexie, { offlineStoreName, keyFieldName, labelFieldNameOrFunction, filterFn }) {
    const start = Date.now()
    const obj = {
      items: [],
      count: 0,
    }
    if (offlineStoreName?.length > 0 && keyFieldName && labelFieldNameOrFunction) {
      const store = dexie.table(offlineStoreName)
      const q = params?.search?.toLowerCase() || ''
      const selectedKeys = params?.externalIds || params?.selectedExternalIds || []
      const count = this.#getPageSize(params)
      const keyedItems =
        selectedKeys.length === 0 || typeof keyFieldName !== 'string'
          ? []
          : await store.where(keyFieldName).anyOfIgnoreCase(selectedKeys).toArray()
      const remainder = count - keyedItems.length
      const matchedItems = []
      if (remainder > 0) {
        matchedItems.push(
          ...(await store
            .filter(
              filterFn ??
                ((d) => {
                  const key = extractValueOrTransform(d, keyFieldName)
                  const label = extractValueOrTransform(d, labelFieldNameOrFunction)?.toLowerCase()
                  return selectedKeys.includes(key) || (label.length > 0 && (q.length == 0 || label.includes(q)))
                })
            )
            .limit(remainder)
            .toArray())
        )
      }
      obj.count = count
      obj.items = [...keyedItems, ...matchedItems]
    }
    const elapsed = Date.now() - start
    log(`In ${elapsed} millis`, 'Returning apiService.idbSearch results', obj)
    return obj
  }

  async idbPage(params, dexie, offlineStoreName, keyFieldName, labelFieldNameOrFunction) {
    const start = Date.now()
    const store = dexie.table(offlineStoreName)
    const items = await store
      .filter((d) => {
        const key = d[keyFieldName]
        const q = params?.search?.toLowerCase() || ''
        const selectedKeys = params?.externalIds || params?.selectedExternalIds || []
        let label =
          (typeof labelFieldNameOrFunction === 'function'
            ? labelFieldNameOrFunction(d)
            : d[labelFieldNameOrFunction]) || ''
        label = label?.toLowerCase()
        return selectedKeys.includes(key) || (label.length > 0 && (q.length == 0 || label.includes(q)))
      })
      .toArray()
    const count = this.#getPageSize(params)
    const obj = {
      items: items.slice(0, count),
      count: count,
    }
    const elapsed = Date.now() - start
    log(`In ${elapsed} millis`, 'Returning apiService.idbPage results', obj)
    return obj
  }

  #getPageSize(params) {
    return params?.pageSize || 50
  }
}
